import { useState, useRef, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import debounce from 'lodash.debounce'
import { EditorSaveStatusType } from '../components/MarkdownEditorToolbar'
import { updateArtifactVersion } from '@/artifacts/fetch/put-artifact-version'
import { saveArtifactBeforeUnload } from '@/artifact-editor/fetch/save-artifact-beforeunload'
import { ArtifactsActions } from '@/artifacts/store/slice'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'

type UseArtifactAutoSaveProps = {
  artifactVersionId: string
  conversationId: string
  sessionId: string
  matterId: string | null
  trackEvent: TrackEventFn
}

export function useArtifactAutoSave(props: UseArtifactAutoSaveProps) {
  const { artifactVersionId, conversationId, sessionId, matterId, trackEvent } = props
  const dispatch = useDispatch()

  // ── State ──

  const [editedContent, setEditedContent] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<EditorSaveStatusType>(EditorSaveStatusType.IDLE)

  // ── Refs ──

  const lastSavedContent = useRef<string | null>(null)
  const isUserTyping = useRef(false)
  const pendingSavedStatus = useRef(false)
  const saveIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const pendingContent = useRef<string | null>(null)
  const saveCount = useRef<number>(0)
  const retryAttemptCount = useRef<number>(0)

  // ── Debounced typing-stopped handler ──

  const debouncedTypingStopped = useRef(
    debounce(() => {
      isUserTyping.current = false
      if (pendingSavedStatus.current) {
        setSaveStatus(EditorSaveStatusType.SAVED)
        pendingSavedStatus.current = false
        setTimeout(() => setSaveStatus(EditorSaveStatusType.IDLE), 2000)
      }
    }, 1000)
  ).current

  // ── Save ──

  const performSave = useCallback(
    async (content: string, versionId: string) => {
      if (content === lastSavedContent.current) return

      setSaveStatus(EditorSaveStatusType.SAVING)

      try {
        const savedVersion = await updateArtifactVersion({ artifactVersionId: versionId, content })
        // Keep the store version in sync so its bumped `updated_at` marks any applied template
        // output stale — a later download then re-applies against this edit instead of serving it.
        dispatch(ArtifactsActions.upsertArtifactVersion(savedVersion))
        lastSavedContent.current = content
        saveCount.current += 1

        trackEvent(AnalyticsEvent.ArtifactEditorAutoSaveSuccess, {
          artifactId: versionId,
          artifactVersionId: versionId,
          conversationId,
          sessionId,
          matterId: matterId ?? 'none',
          saveTimestamp: Date.now(),
          contentLength: content.length,
        })

        if (isUserTyping.current) {
          pendingSavedStatus.current = true
        } else {
          setSaveStatus(EditorSaveStatusType.SAVED)
          setTimeout(() => setSaveStatus(EditorSaveStatusType.IDLE), 2000)
        }
      } catch (error) {
        console.error('Failed to auto-save artifact:', error)

        if (error instanceof Error && (error.message.includes('Failed to fetch') || error.message.includes('aborted') || error.name === 'AbortError')) {
          return
        } else if (error instanceof Error && (error.message.includes('network') || error.message.includes('Network') || !navigator.onLine)) {
          setSaveStatus(EditorSaveStatusType.CONNECTION_LOST)
          trackEvent(AnalyticsEvent.ArtifactEditorSaveError, {
            errorType: 'network',
            errorMessage: error.message,
            artifactId: versionId,
            artifactVersionId: versionId,
            conversationId,
            sessionId,
            matterId: matterId ?? 'none',
            retryAttempt: retryAttemptCount.current,
          })
        } else {
          setSaveStatus(EditorSaveStatusType.ERROR)
          trackEvent(AnalyticsEvent.ArtifactEditorSaveError, {
            errorType: 'general',
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
            artifactId: versionId,
            artifactVersionId: versionId,
            conversationId,
            sessionId,
            matterId: matterId ?? 'none',
            retryAttempt: retryAttemptCount.current,
          })
        }
      }
    },
    [trackEvent, conversationId, sessionId, matterId, dispatch]
  )

  // ── Content change handler (5-sec save interval) ──

  const handleContentChange = useCallback(
    (content: string) => {
      setEditedContent(content)
      pendingContent.current = content
      isUserTyping.current = true

      if (saveStatus === EditorSaveStatusType.SAVED || pendingSavedStatus.current) {
        setSaveStatus(EditorSaveStatusType.SAVING)
        pendingSavedStatus.current = false
      }

      if (artifactVersionId) {
        if (!saveIntervalRef.current) {
          saveIntervalRef.current = setInterval(() => {
            if (pendingContent.current && pendingContent.current !== lastSavedContent.current) {
              performSave(pendingContent.current, artifactVersionId)
            }
          }, 5000)
        }
        debouncedTypingStopped()
      }
    },
    [artifactVersionId, performSave, debouncedTypingStopped, saveStatus]
  )

  const handleBlur = useCallback(() => {
    if (artifactVersionId && pendingContent.current && pendingContent.current !== lastSavedContent.current) {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current)
        saveIntervalRef.current = null
      }
      performSave(pendingContent.current, artifactVersionId)
    }
    isUserTyping.current = false
    debouncedTypingStopped.cancel()
  }, [artifactVersionId, performSave, debouncedTypingStopped])

  const handleRetry = useCallback(async () => {
    if (artifactVersionId && pendingContent.current) {
      retryAttemptCount.current += 1
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current)
        saveIntervalRef.current = null
      }
      await performSave(pendingContent.current, artifactVersionId)
    }
  }, [artifactVersionId, performSave])

  // ── Lifecycle ──

  useEffect(() => {
    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current)
      }
      debouncedTypingStopped.cancel()
    }
  }, [debouncedTypingStopped])

  useEffect(() => {
    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      if (artifactVersionId && pendingContent.current && pendingContent.current !== lastSavedContent.current) {
        try {
          const success = await saveArtifactBeforeUnload(artifactVersionId, pendingContent.current)
          if (success) {
            lastSavedContent.current = pendingContent.current
          } else {
            throw new Error('Save failed')
          }
        } catch (error) {
          console.error('Failed to save during beforeunload:', error)
          event.preventDefault()
          event.returnValue = 'You have unsaved changes that will be lost. Are you sure you want to leave?'
          return event.returnValue
        }
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && artifactVersionId && pendingContent.current && pendingContent.current !== lastSavedContent.current) {
        performSave(pendingContent.current, artifactVersionId)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [artifactVersionId, performSave])

  return {
    editedContent,
    setEditedContent,
    saveStatus,
    handleContentChange,
    handleBlur,
    handleRetry,
    performSave,
    lastSavedContent,
    pendingContent,
    saveCount,
  }
}
